/**
 * Helpers for PR preview deployments. Previews are built per-PR and served statically from S3
 * under `/react/<pr-tag>/<app>/` (see .github/actions/deploy). Redirects between apps must carry
 * the tag along, or the user silently falls out of the preview and into the regular build.
 */

/** PR tag (e.g. `pr-123`) when the current page is running a preview build. */
export function getPreviewTag(): string | undefined {
  return window.location.pathname.match(/^\/react\/(pr-\d+)\//)?.[1]
}

/**
 * URL of a deal's Dealroom, targeting the current PR's preview build when this page is itself a
 * preview. The preview build is served from S3 rather than Django, so the deal and region ids
 * travel in the query string instead of the path.
 */
export function getDealroomUrl(regionId: number, dealId: number): string {
  const tag = getPreviewTag()
  if (tag) {
    return `/react/${tag}/dealroom/index.html?regionId=${regionId}&dealId=${dealId}`
  }
  return `/apps/dealroom/${regionId}/${dealId}`
}

/**
 * URL of the overview app, targeting the current PR's preview build when this page is itself a
 * preview. `hash` is the overview hash-router path, e.g. `/deals?tab=templates`.
 */
export function getOverviewUrl(hash?: string): string {
  const tag = getPreviewTag()
  const base = tag ? `/react/${tag}/overview/index.html` : '/apps/overview'
  return hash ? `${base}#${hash}` : base
}

/**
 * URL of the admin app, targeting the current PR's preview build when this page is itself a
 * preview. The preview build is served from S3 rather than Django, so it has no region in its path.
 */
export function getAdminUrl(regionId?: number): string {
  const tag = getPreviewTag()
  if (tag) return `/react/${tag}/admin/index.html`
  return regionId ? `/apps/admin/${regionId}` : '/apps/admin'
}

/**
 * URL of the login app, targeting the current PR's preview build when this page is itself a
 * preview. Both routes use the legacy `#/?next=` hash format — the React login router serves the
 * login page at its root, matching what the Django-served route expects.
 */
export function getLoginUrl(nextParam?: string): string {
  const tag = getPreviewTag()
  if (tag) {
    return `/react/${tag}/login/index.html#/${nextParam ? `?next=${nextParam}` : ''}`
  }
  return nextParam ? `/apps/login#/?next=${nextParam}` : '/apps/login'
}
